import { z } from 'zod';

export const RequestTypeEnum = z.enum(['BUG', 'FEEDBACK', 'FEATURE', 'SUGGESTION']);
export type RequestType = z.infer<typeof RequestTypeEnum>;

/**
 * The DevStride pipeline, adopted verbatim as Feedback Manager's own status
 * vocabulary. One vocabulary rather than two means the sync mapping is the
 * identity function — which is what makes the sync loop provably terminate
 * (see the 2026-08-04 two-way status sync design, D2/D3).
 *
 * Ordered as work actually flows, because both portals derive their pickers
 * from `STATUS_BADGE_MAP`'s key order.
 */
export const RequestStatusEnum = z.enum([
  'NEW_ICEBOX',
  'BACKLOG',
  'BUILD_AND_TEST',
  'VALIDATION',
  'PLAYBACK',
  'STAGING',
  'REVIEW',
  'DONE_RELEASED',
]);
export type RequestStatus = z.infer<typeof RequestStatusEnum>;

export const RequestPriorityEnum = z.enum(['LOW', 'NORMAL', 'HIGH', 'CRITICAL']);
export type RequestPriority = z.infer<typeof RequestPriorityEnum>;

export const CommentVisibilityEnum = z.enum(['SHARED', 'INTERNAL_ADMIN', 'INTERNAL_CLIENT']);
export type CommentVisibility = z.infer<typeof CommentVisibilityEnum>;

/**
 * `DEVSTRIDE` is written only by `StatusHistory.actorType`, when an inbound
 * status change arrives from DevStride. `Comment.authorType` shares this enum
 * but no comment path ever writes it.
 */
export const AuthorTypeEnum = z.enum(['ADMIN', 'CLIENT', 'DEVSTRIDE']);
export type AuthorType = z.infer<typeof AuthorTypeEnum>;

export const KeyTypeEnum = z.enum(['INTEGRATION', 'BACKEND']);
export type KeyType = z.infer<typeof KeyTypeEnum>;

/**
 * Admin Portal roles. `ADMIN` manages the allowlist (`/admin/users`); `USER`
 * cannot. Both retain full access to every other admin surface — applications,
 * keys, requests, integrations — so `USER` is not a read-only role today.
 *
 * Until 2026-07-28 this was OWNER/ADMIN/AGENT. `OWNER` was indistinguishable
 * from `ADMIN` in every code path, so it was removed rather than left as a
 * distinction the code did not honour, and `AGENT` was renamed `USER` to match
 * the screen that manages these rows.
 */
export const AdminRoleEnum = z.enum(['ADMIN', 'USER']);
export type AdminRole = z.infer<typeof AdminRoleEnum>;

export const ButtonModeEnum = z.enum(['ICON', 'TEXT', 'ICON_TEXT']);
export type ButtonMode = z.infer<typeof ButtonModeEnum>;

/**
 * Which channel a Request arrived through. `WIDGET` is the default so every
 * row that predates email intake stays truthful without a backfill.
 */
/**
 * How a Request got here. `ADMIN` is a staff user filing one by hand from the
 * admin portal, and it is deliberately distinct from `API`: `API` means a
 * server-to-server caller holding an integration key, inferred from the absence
 * of an `Origin` header, while `ADMIN` is an authenticated human on a
 * session-guarded route. Folding the two together would make the admin list's
 * Source filter unable to answer "did we raise this ourselves?", which is the
 * question the value exists for.
 */
export const RequestSourceEnum = z.enum(['WIDGET', 'EMAIL', 'API', 'ADMIN']);
export type RequestSource = z.infer<typeof RequestSourceEnum>;

/**
 * All eight members of the Prisma `IntegrationJobType` enum, in schema order.
 *
 * A short mirror here is worse in kind than a short mirror in the portal's
 * types: this is a RUNTIME validator, so an omission does not merely fail to
 * label a row, it REJECTS a job type the database can legally hold. `BRUTUS_RUN`
 * was missing.
 */
export const IntegrationJobTypeEnum = z.enum([
  'REPORTER_EMAIL',
  'DEVSTRIDE_PUSH',
  'DEVSTRIDE_COMMENT',
  'EMAIL_INTAKE',
  'DEVSTRIDE_STATUS_PUSH',
  'DEVSTRIDE_STATUS_PULL',
  'BRUTUS_RUN',
  'DEVSTRIDE_MOVE',
]);
export type IntegrationJobType = z.infer<typeof IntegrationJobTypeEnum>;

export const IntegrationJobStatusEnum = z.enum([
  'PENDING',
  'RUNNING',
  'SUCCEEDED',
  'FAILED',
  'DEAD',
]);
export type IntegrationJobStatus = z.infer<typeof IntegrationJobStatusEnum>;

export const DevStrideSyncStatusEnum = z.enum(['PENDING', 'SYNCED', 'FAILED', 'SKIPPED']);
export type DevStrideSyncStatus = z.infer<typeof DevStrideSyncStatusEnum>;

/**
 * The launcher's silhouette. `RECTANGLE` is the default and reproduces the
 * pre-2026-07-31 rendering exactly, so no existing application changes
 * appearance when this ships.
 *
 * `SQUARE`, `TEARDROP` and `CUSTOM` have a fixed aspect ratio and therefore
 * cannot carry a label — see `resolveShape`'s `iconOnly` flag in `shapes.ts`.
 */
export const LauncherShapeEnum = z.enum([
  'RECTANGLE',
  'SQUARE',
  'PARALLELOGRAM',
  'NOTCH',
  'TEARDROP',
  'CUSTOM',
]);
export type LauncherShape = z.infer<typeof LauncherShapeEnum>;

/**
 * Which launcher an application paints. `BUTTON` is the default for every
 * existing row and every new one: the draggable control that predates the
 * edge tab. `EDGE_TAB` is the fixed right-edge tab, chosen per application.
 *
 * `resolveLauncherStyle` is the fallback the widget and any other reader of
 * `GET /widget/config` share. A missing or unknown value is `BUTTON`, so an
 * older cached config, or a host that does not understand a future value,
 * keeps the button.
 */
export const LauncherStyleEnum = z.enum(['BUTTON', 'EDGE_TAB']);
export type LauncherStyle = z.infer<typeof LauncherStyleEnum>;

export function resolveLauncherStyle(value: unknown): LauncherStyle {
  return value === 'EDGE_TAB' ? 'EDGE_TAB' : 'BUTTON';
}

/**
 * What an `EDGE_TAB` application does on a phone. `EDGE_TAB` keeps the small
 * edge tab and is the default for every existing row and every new one.
 * `BOTTOM_BAR` docks a trigger in the phone's bottom bar. A wide screen keeps
 * the edge tab either way.
 *
 * `resolvePhoneLauncher` is the fallback the widget shares with any other
 * reader of `GET /widget/config`. A missing or unknown value is `EDGE_TAB`.
 */
export const PhoneLauncherEnum = z.enum(['EDGE_TAB', 'BOTTOM_BAR']);
export type PhoneLauncher = z.infer<typeof PhoneLauncherEnum>;

export function resolvePhoneLauncher(value: unknown): PhoneLauncher {
  return value === 'BOTTOM_BAR' ? 'BOTTOM_BAR' : 'EDGE_TAB';
}
